import { useState, useEffect } from 'react';
import { useAuthStore, useRoomsStore } from './store';
import { AuthScreen } from './components/AuthScreen';
import { Sidebar } from './components/Sidebar';
import { ChatWindow } from './components/ChatWindow';
import { GroupInfo } from './components/GroupInfo';
import type { Room } from './types';
import { useWebSocket } from './hooks/useWebSocket';
import { api } from './api/client';
import { requestNotificationPermissionAndGetToken, onForegroundMessage } from './utils/firebase';

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-dark-900 text-center px-8">
      <div className="relative mb-6">
        <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-brand-500/30">
          <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-dark-900 animate-pulse" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Selamat datang di GarutChat</h2>
      <p className="text-dark-400 max-w-xs leading-relaxed">
        Pilih percakapan di kiri atau buat chat baru untuk memulai.
      </p>
      <div className="mt-8 grid grid-cols-3 gap-4 text-center">
        {[
          { icon: '⚡', title: 'Real-time', desc: 'Pesan instan cepat & stabil' },
          { icon: '🔒', title: 'Aman', desc: 'Autentikasi Google OAuth' },
          { icon: '📱', title: 'Responsif', desc: 'Akses di HP & Desktop' },
        ].map(f => (
          <div key={f.title} className="glass rounded-2xl p-4">
            <div className="text-2xl mb-2">{f.icon}</div>
            <div className="text-white text-sm font-semibold">{f.title}</div>
            <div className="text-dark-400 text-xs mt-1">{f.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatApp() {
  const { user } = useAuthStore();
  const [activeRoom, setActiveRoom] = useState<Room | null>(null);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // Global user notification hub — always called unconditionally
  useWebSocket({
    roomId: user ? `user_${user.id}` : null,
  });

  // Setup Firebase Cloud Messaging
  useEffect(() => {
    if (!user) return;

    let isMounted = true;

    const setupFcm = async () => {
      try {
        const token = await requestNotificationPermissionAndGetToken();
        if (token && isMounted) {
          localStorage.setItem('fcm_token', token);
          await api.users.registerFcmToken(token);
        }
      } catch {
        // Silence background registration errors
      }
    };

    setupFcm();

    // Listen to foreground notifications
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || payload.data?.title || 'Pesan Baru';
      const body = payload.notification?.body || payload.data?.body || '';
      const roomId = payload.data?.room_id;
      const tag = payload.data?.message_id || (roomId ? `room_${roomId}` : 'chat_notification');
      const iconUrl = window.location.origin + '/favicon.ico';

      if (Notification.permission === 'granted') {
        try {
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.ready.then((registration) => {
              registration.showNotification(title, {
                body: body,
                icon: iconUrl,
                badge: iconUrl,
                tag: tag,
                renotify: true,
                vibrate: [200, 100, 200],
                data: payload.data,
              } as any);
            });
          } else {
            new Notification(title, {
              body: body,
              icon: iconUrl,
              tag: tag,
            });
          }
        } catch {
          // Ignore foreground notification errors
        }
      }
    });

    // Listen to messaging postMessage from service worker when notification is clicked
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'OPEN_ROOM') {
        const roomId = event.data.roomId;
        if (roomId) {
          const currentRooms = useRoomsStore.getState().rooms;
          const targetRoom = currentRooms.find(r => r.id === roomId);
          if (targetRoom) {
            handleSelectRoom(targetRoom);
          } else {
            // Room might not be loaded yet, fetch it
            api.rooms.get(roomId).then(({ room }) => {
              if (room) {
                useRoomsStore.getState().upsertRoom(room);
                handleSelectRoom(room);
              }
            }).catch(console.error);
          }
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      isMounted = false;
      unsubscribe();
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [user]);

  // Handle mobile browser back button to close active chat
  useEffect(() => {
    const handlePopState = () => {
      if (mobileShowChat) {
        setMobileShowChat(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [mobileShowChat]);

  const handleSelectRoom = (room: Room) => {
    setActiveRoom(room);
    setMobileShowChat(true);
    setShowGroupInfo(false);
    if (window.innerWidth < 640 && !window.history.state?.chatOpen) {
      window.history.pushState({ chatOpen: true }, '');
    }
  };

  const handleRoomUpdated = (room: Room) => {
    setActiveRoom(room);
  };

  const handleBackToList = () => {
    setMobileShowChat(false);
    if (window.history.state?.chatOpen) {
      window.history.back();
    }
  };

  return (
    <div className="flex h-[100dvh] bg-dark-900 overflow-hidden pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]">
      {/* Sidebar — hidden on mobile when chat is open, shown on desktop always */}
      <div className={`
        flex-shrink-0 h-full flex-col bg-dark-800 border-r border-dark-700
        ${mobileShowChat ? 'hidden sm:flex' : 'flex'}
        w-full sm:w-80 lg:w-96
      `}>
        <Sidebar onSelectRoom={handleSelectRoom} />
      </div>

      {/* Chat area */}
      <div className={`
        flex-1 flex-col h-full relative
        ${!mobileShowChat ? 'hidden sm:flex' : 'flex'}
      `}>
        {activeRoom ? (
          <ChatWindow
            room={activeRoom}
            onOpenGroupInfo={() => setShowGroupInfo(true)}
            onBack={handleBackToList}
          />
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Group Info Modal */}
      {showGroupInfo && activeRoom && activeRoom.type === 'group' && (
        <GroupInfo
          room={activeRoom}
          onClose={() => setShowGroupInfo(false)}
          onUpdated={handleRoomUpdated}
        />
      )}
    </div>
  );
}

export default function App() {
  const { user } = useAuthStore();
  if (!user) return <AuthScreen />;
  return <ChatApp />;
}
